import type { LobbyState } from '@multirx/shared/types/championship.js';
import { canPlayOtherDriver } from '@multirx/shared/types/user.js';
import { nationalityFlagMarkup } from '../components/nationalityFlag.js';
import { createPillButton } from '../ui/pillButton.js';
import { labelOwnDriver } from '../auth/ownDriverLabel.js';
import { getCurrentUser } from '../auth/session.js';
import { fetchMeetingSession, releaseMeetingClaim } from './api.js';
import { getClientClaimerId, setLastChosenDriverId } from './seasonPrefs.js';
import { setLastBoardState } from '../board/boardStateCache.js';
import {
  connectMeetingLobby,
  emitMeetingClaim,
  emitMeetingReady,
  emitMeetingStart,
} from './meetingSocket.js';
import { openAlertModal } from '../ui/confirmModal.js';

function showLobbyAlert(host: HTMLElement, message: string): void {
  void openAlertModal(host, { title: 'Salon', message });
}

export type DriverLobbyPanelOptions = {
  sessionId: number;
  onLaunch: (driverId: number) => void;
  /** Le tour de chauffe démarre pour un humain de la manche (server-autoritaire). */
  onWarmupStart?: (driverId: number) => void;
  onCancel?: () => void;
  onSimulateDone?: () => void;
  /**
   * Solo : pas d’étape « Se déclarer prêt » séparée — « C'est parti ! » claim+ready+start.
   * Multi : claim au clic, ready + lancer via les boutons (toujours confirmation explicite).
   * @deprecated auto-launch au clic a été retiré ; le flag ne contrôle plus qu’UX ready.
   */
  autoLaunchOnSelect?: boolean;
};

function escapeHtml(s: string): string {
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}

export function mountDriverLobbyPanel(
  host: HTMLElement,
  options: DriverLobbyPanelOptions
): { refresh: () => Promise<void>; destroy: () => void } {
  const claimerId = getClientClaimerId();
  const authUser = getCurrentUser();
  const ownDriverId = authUser?.driverId ?? null;
  const isStaff = authUser?.role != null && canPlayOtherDriver(authUser.role);
  const forceOwnDriver = !isStaff && ownDriverId != null;
  let lobby: LobbyState | null = null;
  let selectedDriverId: number | null = null;
  let didPrefill = false;
  let launching = false;

  const ownOnGrid = (): boolean =>
    ownDriverId != null && (lobby?.drivers.some((d) => d.slot.driverId === ownDriverId) ?? false);

  const render = (): void => {
    if (!lobby) {
      host.innerHTML = '<p class="championship-lobby-loading">Chargement du salon…</p>';
      return;
    }

    host.innerHTML = '';
    const wrap = document.createElement('div');
    wrap.className = 'championship-lobby';

    const head = document.createElement('div');
    head.className = 'championship-lobby-head';
    const forced = forceOwnDriver && ownOnGrid();
    const hint = forced
      ? '<p class="championship-lobby-hint">Vous jouez votre pilote. Confirmez pour lancer.</p>'
      : '<p class="championship-lobby-hint">Choisissez un pilote, puis cliquez sur « C\'est parti ! ».</p>';
    const waiting = options.autoLaunchOnSelect
      ? ''
      : `<p class="championship-lobby-waiting">${escapeHtml(lobby.waitingLabel)}</p>`;
    head.innerHTML = `
      <h2 class="championship-lobby-title">Choix du pilote</h2>
      ${hint}
      ${waiting}
    `;
    wrap.appendChild(head);

    const grid = document.createElement('div');
    grid.className = 'championship-lobby-grid';
    for (const d of lobby.drivers) {
      const driverId = d.slot.driverId;
      const isOwn = ownDriverId != null && driverId === ownDriverId;
      if (forced && !isOwn) continue;

      const taken = d.taken && d.takenByClaimerId !== claimerId;
      const isMine = d.takenByClaimerId === claimerId;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `championship-lobby-card${taken ? ' is-taken' : ''}${isMine ? ' is-mine' : ''}${selectedDriverId === driverId ? ' is-selected' : ''}`;
      btn.disabled = taken || (forced && !isOwn);
      const flag = d.slot.nationality
        ? nationalityFlagMarkup(d.slot.nationality, { className: 'championship-lobby-flag' })
        : '';
      btn.innerHTML = `
        <span class="championship-lobby-card-top">
          ${isMine || taken ? '<span class="championship-lobby-check" aria-hidden="true">✓</span>' : ''}
          ${flag}
          <span class="championship-lobby-name">${escapeHtml(labelOwnDriver(d.slot.driverName, driverId))}</span>
        </span>
        <span class="championship-lobby-car">${escapeHtml(d.slot.carName)}</span>
      `;
      if (!forced) {
        btn.addEventListener('click', () => {
          selectDriver(driverId);
        });
      }
      grid.appendChild(btn);
    }
    wrap.appendChild(grid);

    const actions = document.createElement('div');
    actions.className = 'championship-lobby-actions';
    const myClaim = lobby.claims.find((c) => c.claimerId === claimerId);
    const hasSelection = selectedDriverId != null || myClaim != null;

    if (options.onCancel) {
      actions.appendChild(
        createPillButton('Retour', { variant: 'soft', onClick: options.onCancel })
      );
    }

    if (!options.autoLaunchOnSelect && myClaim) {
      const readyBtn = createPillButton(myClaim.ready ? 'Prêt ✓' : 'Se déclarer prêt', {
        variant: 'soft',
      });
      if (readyBtn instanceof HTMLButtonElement) {
        readyBtn.onclick = () => toggleReady();
      }
      actions.appendChild(readyBtn);
    }

    if (options.autoLaunchOnSelect) {
      const goBtn = createPillButton("C'est parti !", { variant: 'cta', large: true });
      if (goBtn instanceof HTMLButtonElement) {
        goBtn.disabled = !hasSelection || launching;
        goBtn.onclick = () => confirmGoSolo();
      }
      actions.appendChild(goBtn);
    } else if (myClaim?.ready) {
      const launchBtn = createPillButton("C'est parti !", { variant: 'cta', large: true });
      if (launchBtn instanceof HTMLButtonElement) {
        launchBtn.disabled = !lobby.canStart || launching;
        launchBtn.onclick = () => {
          if (!myClaim) return;
          launching = true;
          setLastChosenDriverId(myClaim.driverId);
          emitMeetingStart(options.sessionId, claimerId);
          render();
        };
      }
      actions.appendChild(launchBtn);
    }
    wrap.appendChild(actions);

    host.appendChild(wrap);
  };

  /** Claim sans ready — la confirmation « C'est parti ! » gère ready+start. */
  const selectDriver = (driverId: number): void => {
    if (forceOwnDriver && ownDriverId != null && driverId !== ownDriverId) {
      showLobbyAlert(host, 'Vous ne pouvez jouer que votre propre pilote');
      return;
    }
    try {
      emitMeetingClaim(options.sessionId, {
        driverId,
        claimerId,
        displayName: 'Joueur',
        ready: false,
      });
      selectedDriverId = driverId;
      render();
    } catch (e) {
      showLobbyAlert(host, e instanceof Error ? e.message : 'Choix impossible');
    }
  };

  const confirmGoSolo = (): void => {
    const driverId =
      selectedDriverId ?? lobby?.claims.find((c) => c.claimerId === claimerId)?.driverId ?? null;
    if (driverId == null || launching) return;
    if (forceOwnDriver && ownDriverId != null && driverId !== ownDriverId) {
      showLobbyAlert(host, 'Vous ne pouvez jouer que votre propre pilote');
      return;
    }
    launching = true;
    render();
    try {
      emitMeetingClaim(options.sessionId, {
        driverId,
        claimerId,
        displayName: 'Joueur',
        ready: true,
      });
      selectedDriverId = driverId;
      setLastChosenDriverId(driverId);
      emitMeetingStart(options.sessionId, claimerId);
    } catch (e) {
      launching = false;
      showLobbyAlert(host, e instanceof Error ? e.message : 'Lancement impossible');
      render();
    }
  };

  const toggleReady = (): void => {
    const myClaim = lobby?.claims.find((c) => c.claimerId === claimerId);
    if (!myClaim) return;
    try {
      emitMeetingReady(options.sessionId, claimerId, !myClaim.ready);
    } catch (e) {
      showLobbyAlert(host, e instanceof Error ? e.message : 'Erreur');
    }
  };

  const maybePrefillOwnDriver = (): void => {
    if (didPrefill || !lobby || ownDriverId == null) return;
    const slot = lobby.drivers.find((d) => d.slot.driverId === ownDriverId);
    if (!slot) return;
    const takenByOther = slot.taken && slot.takenByClaimerId !== claimerId;
    if (takenByOther) return;
    didPrefill = true;
    selectDriver(ownDriverId);
  };

  const refresh = async (): Promise<void> => {
    const data = await fetchMeetingSession(options.sessionId, claimerId);
    lobby = data.lobby;
    maybePrefillOwnDriver();
    render();
  };

  const socketTeardown = connectMeetingLobby(options.sessionId, claimerId, {
    onLobby: (state) => {
      lobby = state;
      const myClaim = state.claims.find((c) => c.claimerId === claimerId);
      if (myClaim) selectedDriverId = myClaim.driverId;
      maybePrefillOwnDriver();
      render();
    },
    onPlayConfig: () => {
      const myDriverId = lobby?.claims.find((c) => c.claimerId === claimerId)?.driverId;
      if (myDriverId == null) return;
      setLastChosenDriverId(myDriverId);
      options.onLaunch(myDriverId);
    },
    onWarmupStart: (state) => {
      const myDriverId = lobby?.claims.find((c) => c.claimerId === claimerId)?.driverId;
      if (myDriverId == null) return;
      const mySlot = lobby?.drivers.find((d) => d.slot.driverId === myDriverId);
      if (!mySlot || !state.playerIsHuman[mySlot.slot.playerIndex]) return;
      setLastBoardState(options.sessionId, state);
      options.onWarmupStart?.(myDriverId);
    },
    onError: (msg) => {
      launching = false;
      void openAlertModal(host, { title: 'Salon', message: msg }).then(() => {
        if (/déjà terminée|introuvable/i.test(msg)) {
          options.onCancel?.();
          window.dispatchEvent(new CustomEvent('multirx-championship-updated'));
        }
      });
    },
  });

  const destroy = (): void => {
    socketTeardown();
    void releaseMeetingClaim(options.sessionId, { claimerId }).catch(() => {});
    host.innerHTML = '';
  };

  void refresh();
  return { refresh, destroy };
}
